import { createContext, useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import userDefaultValues from "./userDefaultValues";
import Swal from "sweetalert2";
import { useTranslation } from "react-i18next";

const AuthContext = createContext();

export default AuthContext;

export const AuthProvider = ({ children }) => {
    const { t } = useTranslation("authenticate");
    const [user, setUser] = useState(null);
    const [authTokens, setAuthTokens] = useState(null);
    const [org, setOrg] = useState(null);
    const [panel, setPanel] = useState(null);
    const [azureMapsKey, setAzureMapsKey] = useState(localStorage.getItem("azureMapsKey"));
    const [pendingOtp, setPendingOtp] = useState(null);
    const navigate = useNavigate();
    // detectar recursion por cambio de panel desde el navegador se dispara dos veces
    // por la recepcion del evento en Websocket y el navegador
    const lastNavigationRef = useRef({ panelToken: null, timestamp: 0 });
    const apiUrl = process.env.REACT_APP_API_URL;

    // recovery user by JWT token
    useEffect(() => {
        if (user) return;
        const token = localStorage.getItem("token");
        if (token) {
            axios.defaults.headers["Authorization"] = `Bearer ${token}`;
            axios
                .get(`/api/user/recovery/`)
                .then((resp) => {
                    if (resp.status === 200 && resp.data) {
                        //console.log(resp.data.user)

                        setAuthTokens({
                            access: resp.data.access,
                            refresh: resp.data.refresh,
                        });
                        setUser(resp.data.user);
                        setOrg(resp.data.user.organization);
                        setPanel(resp.data.user.panel);
                        // -
                        localStorage.setItem("token", resp.data.access);
                        localStorage.setItem("refreshToken", resp.data.refresh);
                        localStorage.setItem("azureMapsKey", resp.data.azure_maps_key);
                        setAzureMapsKey(resp.data.azure_maps_key);
                    } else {
                        console.info("[AuthP.uE] No se ha podido recuperar el usuario");
                        navigate("/login/");
                    }
                })
                .catch(function (error) {
                    //console.log(error.toJSON());
                    //navigate("/login/");
                    return false;
                });
        }
    }, [user, apiUrl, navigate, setAuthTokens, setUser, setOrg, setPanel]);

    const loginUser = async (e) => {
        e.preventDefault();
        try {
            const storedDeviceToken = localStorage.getItem("device_token") || undefined;
            const { data, status } = await axios.post(`/api/login/`, {
                email: e.target.email.value,
                password: e.target.password.value,
                ...(storedDeviceToken ? { device_token: storedDeviceToken } : {}),
            });

            if (data.requires_otp) {
                setPendingOtp({ email: e.target.email.value, password: e.target.password.value, otp_type: data.otp_type || "app" });
                return Promise.resolve(data);
            } else if (data.renew) {
                Swal.fire({
                    title: t("title.outdated-passwd"),
                    text: t("messages.password-expired"),
                    icon: "warning",
                    target: "body",
                    allowOutsideClick: false,
                    allowEscapeKey: false,
                    confirmButtonText: t("labels.change"),
                }).then(() => {
                    navigate(`/renew/password/${data.token}/`);
                });
            } else if (status === 200 && !data.check) {
                return Promise.resolve(data);
            } else if (status === 200 && data.trusted) {
                // Dispositivo de confianza: completar login directamente sin pedir 2FA
                axios.defaults.headers["Authorization"] = `Bearer ${data.access}`;
                setAuthTokens({ access: data.access, refresh: data.refresh });
                localStorage.setItem("token", data.access);
                localStorage.setItem("refreshToken", data.refresh);
                localStorage.setItem("azureMapsKey", data.azure_maps_key);
                setAzureMapsKey(data.azure_maps_key);
                const userData = data.user;
                if (!localStorage.getItem(userData.token)) {
                    userData["default"] = userDefaultValues;
                    localStorage.setItem(userData.token, JSON.stringify(userData));
                }
                setUser(userData);
                setOrg(userData.organization);
                setPanel(userData.panel);
                if (!userData.organization || !userData.panel) navigate("notorgpanel/");
                return Promise.resolve(data);
            } else if (status === 200 && data) {
                axios.defaults.headers["Authorization"] = `Bearer ${data.access}`;
                setPendingOtp({ setup: true, access: data.access, refresh: data.refresh, user: data.user, azure_maps_key: data.azure_maps_key });
                return Promise.resolve(data);
            } else {
                // console.log("Error AuthContext")
                // console.error(data);
                return Promise.reject(data);
            }
        } catch (error) {
            // console.log("Error AuthContext")
            // console.error(error);
            return Promise.reject(error);
        }
    };

    const logoutUser = async (e) => {
        if (e) {
            e.preventDefault();
        }
        axios
            .post(`/api/logout/`, {
                refresh_token: localStorage.getItem("refreshToken"),
            })
            .then((resp) => {
                // Clear localStorage
                localStorage.removeItem("token");
                localStorage.removeItem("refreshToken");
                localStorage.removeItem(user.token);
                setAuthTokens(null);
                setUser(null);
                setOrg(null);
                setPanel(null);
                setAzureMapsKey(null);
                localStorage.removeItem("azureMapsKey");
            })
            .catch((error) => {
                console.error("Error interno del servidor:", error.message);
            });

        // Redirect to the desired location
        navigate("/login/");
    };

    const completeSetupLogin = () => {
        const { access, refresh, user: userData, azure_maps_key } = pendingOtp;
        setAuthTokens({ access, refresh });
        localStorage.setItem("token", access);
        localStorage.setItem("refreshToken", refresh);
        localStorage.setItem("azureMapsKey", azure_maps_key);
        setAzureMapsKey(azure_maps_key);
        if (!localStorage.getItem(userData.token)) {
            userData["default"] = userDefaultValues;
            localStorage.setItem(userData.token, JSON.stringify(userData));
        }
        setUser(userData);
        setPendingOtp(null);
        if (!userData.organization || !userData.panel) navigate("notorgpanel/");
        setOrg(userData.organization);
        setPanel(userData.panel);
        navigate("/");
    };

    const verifyOtp = async (code) => {
        const { data, status: httpStatus } = await axios.post("/api/two-factor/verify/", {
            email: pendingOtp.email,
            password: pendingOtp.password,
            code,
            trust_device: true,
        }).catch(err => err.response);
        if (httpStatus === 200 && data.status) {
            if (data.device_token) {
                localStorage.setItem("device_token", data.device_token);
            }
            setPendingOtp(null);
            setAuthTokens({ access: data.access, refresh: data.refresh });
            localStorage.setItem("token", data.access);
            localStorage.setItem("refreshToken", data.refresh);
            localStorage.setItem("azureMapsKey", data.azure_maps_key);
            setAzureMapsKey(data.azure_maps_key);
            const userData = data.user;
            if (!localStorage.getItem(userData.token)) {
                userData["default"] = userDefaultValues;
                localStorage.setItem(userData.token, JSON.stringify(userData));
            }
            setUser(userData);
            if (!userData.organization || !userData.panel) {
                navigate("notorgpanel/");
            }
            setOrg(userData.organization);
            setPanel(userData.panel);
            navigate("/");
            return { status: true };
        }
        return { status: false };
    };

    // UUID hexadecimal de 16 caracteres
    const HEX_PATTERN_UUID = /\/[0-9a-fA-F]{16}(\/)?$/;
    // Token alfanumérico de 32 caracteres
    const TOKEN_PATTERN = /\/[0-9a-z]{32}(\/)?$/i;
    // ID numérico (cualquier longitud)
    const ID_PATTERN = /\/\d+(\/)?$/;

    const updatePanel = (panel) => {
        const currentPath = window.location.pathname;

        // Verificar los tres patrones
        const hasHexUUID = HEX_PATTERN_UUID.test(currentPath);
        const hasToken = TOKEN_PATTERN.test(currentPath);
        const hasID = ID_PATTERN.test(currentPath);
        const hasPattern = hasHexUUID || hasToken || hasID;

        setPanel(panel);

        const updatedUser = {
            ...user,
            panel_id: panel.token,
            panel: panel,
        };
        setUser(updatedUser);

        // Evitar navegaciones duplicadas en un intervalo corto (500ms) por Websocket
        const now = Date.now();
        const timeSinceLastNav = now - lastNavigationRef.current.timestamp;
        const isSamePanel = lastNavigationRef.current.panelToken === panel.token;

        if (isSamePanel && timeSinceLastNav < 500) {
            //console.log("Navegación duplicada detectada, saltando (mismo panel en <500ms)");
            return;
        }

        // Actualizar referencia
        lastNavigationRef.current = {
            panelToken: panel.token,
            timestamp: now,
        };

        if (currentPath !== "/" && hasPattern) {
            const pathParts = currentPath.split("/").filter((part) => part !== "");
            const newPathParts = pathParts.slice(0, -2);
            const cleanPath = "/" + newPathParts.join("/") + "/";
            navigate(cleanPath || "/", { replace: true });
        } else {
            navigate(0);
        }
    };

    const contextData = {
        user: user,
        authTokens: authTokens,
        org: org,
        panel: panel,
        loginUser: loginUser,
        logoutUser: logoutUser,
        updatePanel: updatePanel,
        azureMapsKey: azureMapsKey,
        pendingOtp: pendingOtp,
        verifyOtp: verifyOtp,
        completeSetupLogin: completeSetupLogin,
    };

    return <AuthContext.Provider value={{ ...contextData }}>{children}</AuthContext.Provider>;
};
